import moment from 'moment';

export interface DatePreset {
    label: string;
    category: 'Weeks' | 'Months' | 'Years';
    getValue: () => [moment.Moment, moment.Moment];
    getLabel?: () => string;
}

const generateDynamicLabel = (preset: DatePreset): string => {
    return preset.getLabel ? preset.getLabel() : preset.label;
};

export const DATE_PRESETS: DatePreset[] = [
    {
        label: "Last week",
        category: "Weeks",
        getValue: () => [
            moment().subtract(1, 'week').startOf('week'),
            moment().subtract(1, 'week').endOf('week')
        ]
    },
    {
        label: "This week",
        category: "Weeks",
        getValue: () => [moment().startOf('week'), moment().endOf('week')]
    },
    {
        label: "Last 7 days",
        category: "Weeks",
        getValue: () => [moment().subtract(7, 'days'), moment()]
    },
    {
        label: "Last 14 days",
        category: "Weeks",
        getValue: () => [moment().subtract(14, 'days'), moment()]
    },
    {
        label: "Last 30 days",
        category: "Weeks",
        getValue: () => [moment().subtract(30, 'days'), moment()]
    },
    {
        label: "This month",
        category: "Months",
        getValue: () => [moment().startOf('month'), moment().endOf('month')],
        getLabel: () => `This month (${moment().format('MMMM')})`
    },
    {
        label: "Last month",
        category: "Months",
        getValue: () => [
            moment().subtract(1, 'month').startOf('month'),
            moment().subtract(1, 'month').endOf('month')
        ],
        getLabel: () => `Last month (${moment().subtract(1, 'month').format('MMMM')})`
    },
    {
        label: "Last 3 months",
        category: "Months",
        getValue: () => [moment().subtract(3, 'months'), moment()]
    },
    {
        label: "Last 6 months",
        category: "Months",
        getValue: () => [moment().subtract(6, 'months'), moment()]
    },
    {
        label: "Last 12 months",
        category: "Months",
        getValue: () => [moment().subtract(12, 'months'), moment()]
    },
    {
        label: "This year",
        category: "Years",
        getValue: () => [moment().startOf('year'), moment().endOf('year')],
        getLabel: () => `This year (${moment().format('YYYY')})`
    },
    {
        label: "Last year",
        category: "Years",
        getValue: () => [
            moment().subtract(1, 'year').startOf('year'),
            moment().subtract(1, 'year').endOf('year')
        ],
        getLabel: () => `Last year (${moment().subtract(1, 'year').format('YYYY')})`
    }
];

export const getDateRangeFromPreset = (presetLabel: string): Date[] => {
    let preset = DATE_PRESETS.find(p => generateDynamicLabel(p) === presetLabel);

    if (!preset) {
        preset = DATE_PRESETS.find(p => p.label === presetLabel);
    }

    if (!preset) {
        return [moment().subtract(7, 'days').toDate(), moment().toDate()];
    }

    const [from, to] = preset.getValue();
    return [from.toDate(), to.toDate()];
};

export const getPresetsByCategory = (category: 'Weeks' | 'Months' | 'Years') => {
    return DATE_PRESETS
        .filter(p => p.category === category)
        .map(p => generateDynamicLabel(p));
};

export const findMatchingPreset = (from: Date, to: Date): string | null => {
    const fromStr = moment(from).format('YYYY-MM-DD');
    const toStr = moment(to).format('YYYY-MM-DD');

    const matchingPreset = DATE_PRESETS.find(preset => {
        const [presetFrom, presetTo] = preset.getValue();
        return moment(presetFrom).format('YYYY-MM-DD') === fromStr &&
            moment(presetTo).format('YYYY-MM-DD') === toStr;
    });

    return matchingPreset ? generateDynamicLabel(matchingPreset) : null;
};

export const detectCategoryFromLabel = (label: string): 'Weeks' | 'Months' | 'Years' | 'Custom' => {
    if (label.includes("-") &&
        !label.includes("days") &&
        !label.includes("week") &&
        !label.includes("month") &&
        !label.includes("year")) {
        return "Custom";
    }

    let preset = DATE_PRESETS.find(p => generateDynamicLabel(p) === label);

    if (!preset) {
        preset = DATE_PRESETS.find(p => p.label === label);
    }

    return preset?.category || "Weeks";
};